// src/components/lessons/renderers/OutlineRenderer.jsx
import React, { useState } from 'react';
import { FileText } from 'lucide-react';

const OutlineRenderer = ({ block, isMobile }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const processText = (text) => {
    if (typeof text !== 'string') return text;
    return text.replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 700;">$1</strong>');
  };

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '1.5rem'
      }}>
        {block.parts && block.parts.map((part, i) => (
          <div
            key={i}
            style={{
              background: hoveredIndex === i ? '#000000' : '#FFFFFF',
              borderRadius: '0.75rem',
              boxShadow: hoveredIndex === i ? '0 8px 24px rgba(0, 0, 0, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
              border: '2px solid #000000',
              padding: isMobile ? '1.25rem' : '1.5rem',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: hoveredIndex === i ? 'translateY(-4px) scale(1.02)' : 'translateY(0) scale(1)',
              cursor: 'default',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={() => setHoveredIndex(i)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            {/* Blue accent line at top */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: '#3A86FF'
            }} />

            {/* Part name header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              fontWeight: '700',
              fontSize: isMobile ? '1rem' : '1.125rem',
              color: hoveredIndex === i ? '#FFFFFF' : '#000000',
              borderBottom: hoveredIndex === i ? '2px solid rgba(255,255,255,0.2)' : '2px solid #E5E7EB',
              paddingBottom: '0.75rem',
              marginBottom: '1rem',
              marginTop: '0.5rem',
              transition: 'all 0.3s ease'
            }}>
              <div style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: hoveredIndex === i ? '#3A86FF' : '#000000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                transform: hoveredIndex === i ? 'rotate(12deg)' : 'rotate(0deg)'
              }}>
                <FileText style={{
                  width: '14px',
                  height: '14px',
                  color: hoveredIndex === i ? '#000000' : '#3A86FF'
                }} />
              </div>
              {part.name}
            </div>

            {/* Keywords section */}
            <div style={{ marginBottom: '1rem' }}>
              <span style={{
                fontWeight: '700',
                fontSize: isMobile ? '0.8rem' : '0.85rem',
                color: hoveredIndex === i ? '#FFFFFF' : '#000000',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                opacity: 0.7,
                transition: 'color 0.3s ease'
              }}>
                Keywords:
              </span>
              <p style={{
                color: hoveredIndex === i ? '#FFFFFF' : '#000000',
                fontSize: isMobile ? '0.85rem' : '0.9rem',
                marginTop: '0.5rem',
                lineHeight: '1.6',
                opacity: hoveredIndex === i ? 0.9 : 0.8,
                transition: 'all 0.3s ease'
              }}>
                {part.keywords.join(', ')}
              </p>
            </div>

            {/* Redemittel phrase */}
            <div style={{
              marginTop: 'auto',
              background: hoveredIndex === i ? '#FFFFFF' : '#FAFBFC',
              padding: isMobile ? '1rem' : '1.25rem',
              borderRadius: '0.5rem',
              border: hoveredIndex === i ? '1px solid #E5E7EB' : '1px solid #E5E7EB',
              borderLeft: hoveredIndex === i ? '3px solid #000000' : '3px solid #3A86FF',
              transition: 'all 0.3s ease'
            }}>
              <span style={{
                fontWeight: '700',
                fontSize: isMobile ? '0.8rem' : '0.85rem',
                color: hoveredIndex === i ? '#000000' : '#3A86FF',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                display: 'block',
                marginBottom: '0.5rem',
                transition: 'color 0.3s ease'
              }}>
                Phrase:
              </span>
              <p
                style={{
                  fontSize: isMobile ? '0.85rem' : '0.9rem',
                  color: '#000000',
                  fontStyle: 'italic',
                  lineHeight: '1.6',
                  margin: 0,
                  opacity: 0.9
                }}
                dangerouslySetInnerHTML={{
                  __html: `"${processText(part.redemittel)}"`
                }}
              />
            </div>

            {/* Decorative dot */}
            {hoveredIndex === i && (
              <div style={{
                position: 'absolute',
                bottom: '12px',
                right: '12px',
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: '#3A86FF',
                animation: 'pulse 1.5s ease-in-out infinite'
              }} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default OutlineRenderer;